import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Form, Button, Radio, Checkbox, Input, Select, message, Spin, Alert, Row, Col, Typography } from 'antd';
import { useAuth } from '../context/AuthContext';
import {
  relationshipStatusOptions,
  livingSituationOptions,
  educationLevelOptions,
  employmentStatusOptions,
  workEnvironmentOptions,
  substanceUseStatusOptions
} from '../utils/lifestyleHistoryOptions'; // Import options

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const StructuredLifestyleHistoryForm = ({ patientId, onSaveSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true); // Separate state for initial fetch
  const [error, setError] = useState(null);
  const { user } = useAuth();

  // Fetch existing data
  const fetchData = useCallback(async () => {
    if (!patientId) {
      setFetching(false);
      return;
    }
    setFetching(true);
    setError(null);
    try {
      const response = await axios.get(`/patients/${patientId}`);
      const patientData = response.data.data.patient;
      const lifestyleData = patientData?.structuredLifestyleHistory || {};

      // Pre-fill form - handle potential undefined nested objects
      form.setFieldsValue({
        relationshipStatus: lifestyleData.relationshipStatus,
        livingSituation: lifestyleData.livingSituation,
        educationLevel: lifestyleData.educationLevel,
        employmentStatus: lifestyleData.employmentStatus,
        workEnvironment: lifestyleData.workEnvironment || [], // Default to empty array for checkbox group
        tobaccoStatus: lifestyleData.tobaccoUse?.status || 'Never',
        tobaccoDetails: lifestyleData.tobaccoUse?.details,
        alcoholStatus: lifestyleData.alcoholUse?.status || 'Never',
        alcoholDetails: lifestyleData.alcoholUse?.details,
        drugStatus: lifestyleData.drugUse?.status || 'Never',
        drugDetails: lifestyleData.drugUse?.details,
        occupation: lifestyleData.occupation,
        notes: lifestyleData.notes,
      });
    } catch (err) {
      console.error("Error fetching lifestyle history:", err);
      setError(err.response?.data?.message || 'Failed to fetch existing data.');
    } finally {
      setFetching(false);
    }
  }, [patientId, form]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Save
  const handleSave = async (values) => {
    if (!user || !user._id) {
      message.error("User not authenticated.");
      return;
    }
    setLoading(true);
    setError(null);

    // Structure the payload according to the backend model
    const payload = {
      structuredLifestyleHistory: {
        relationshipStatus: values.relationshipStatus,
        livingSituation: values.livingSituation,
        educationLevel: values.educationLevel,
        employmentStatus: values.employmentStatus,
        workEnvironment: values.workEnvironment || [], // Ensure it's an array
        tobaccoUse: {
          status: values.tobaccoStatus,
          details: values.tobaccoDetails,
        },
        alcoholUse: {
          status: values.alcoholStatus,
          details: values.alcoholDetails,
        },
        drugUse: {
          status: values.drugStatus,
          details: values.drugDetails,
        },
        occupation: values.occupation,
        notes: values.notes,
        lastUpdatedBy: user._id, // Add user ID
        // lastUpdatedAt will be set by the backend pre-save hook
      }
    };

    try {
      await axios.patch(`/patients/${patientId}`, payload);
      message.success('Lifestyle History Checklist updated successfully!');
      if (onSaveSuccess) {
        onSaveSuccess(); // Callback to close modal and potentially refresh data
      }
    } catch (err) {
      console.error("Error saving lifestyle history:", err);
      message.error(err.response?.data?.message || 'Failed to save checklist.');
      setError(err.response?.data?.message || 'Failed to save checklist.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={fetching || loading}>
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Form form={form} layout="vertical" onFinish={handleSave}>

        <Title level={5}>Tình trạng quan hệ</Title>
        <Form.Item name="relationshipStatus">
          <Radio.Group options={relationshipStatusOptions} />
        </Form.Item>

        <Title level={5}>Tình trạng cư trú</Title>
        <Form.Item name="livingSituation">
          <Radio.Group options={livingSituationOptions} />
        </Form.Item>

        <Title level={5}>Trình độ học vấn</Title>
        <Form.Item name="educationLevel">
          <Radio.Group options={educationLevelOptions} />
        </Form.Item>

        <Title level={5}>Tình trạng nghề nghiệp</Title>
        <Form.Item name="employmentStatus">
          <Radio.Group options={employmentStatusOptions} />
        </Form.Item>

        <Title level={5}>Môi trường làm việc</Title>
        <Form.Item name="workEnvironment">
          <Checkbox.Group options={workEnvironmentOptions} />
        </Form.Item>

        <Title level={5}>Sử dụng chất</Title>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="tobaccoStatus" label="Thuốc lá">
              <Select>
                {substanceUseStatusOptions.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item name="tobaccoDetails" label="Chi tiết (Thuốc lá)">
              <Input placeholder="Số lượng/ngày, thời gian sử dụng..." />
            </Form.Item>
          </Col>
        </Row>
         <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="alcoholStatus" label="Rượu bia">
              <Select>
                {substanceUseStatusOptions.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item name="alcoholDetails" label="Chi tiết (Rượu bia)">
              <Input placeholder="Số lượng/tuần, loại..." />
            </Form.Item>
          </Col>
        </Row>
         <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name="drugStatus" label="Chất kích thích khác">
              <Select>
                {substanceUseStatusOptions.map(opt => <Option key={opt} value={opt}>{opt}</Option>)}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item name="drugDetails" label="Chi tiết (Chất kích thích)">
              <Input placeholder="Loại, tần suất..." />
            </Form.Item>
          </Col>
        </Row>

        <Title level={5}>Nghề nghiệp</Title>
        <Form.Item name="occupation" label="Công việc hiện tại/trước đây">
          <Input />
        </Form.Item>

        <Title level={5}>Ghi chú chung</Title>
        <Form.Item name="notes" label="Ghi chú khác về lối sống">
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item style={{ textAlign: 'right', marginTop: 20 }}>
          <Button onClick={onCancel} style={{ marginRight: 8 }} disabled={loading}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Save Checklist
          </Button>
        </Form.Item>
      </Form>
    </Spin>
  );
};

export default StructuredLifestyleHistoryForm;
